import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// ZIFEK CORE — 71. MIGRATIONS
// TOUTES les modifications de schéma doivent être VERSIONNÉES.
// On ne modifie JAMAIS la base en production sans migration contrôlée.
// Prévoir : migration · rollback lorsque possible · version · documentation.
// ─────────────────────────────────────────────────────────────

export interface CoreMigration {
  id: number;
  version: string;
  name: string | null;
  checksum: string | null;
  status: string | null;
  description: string | null;
  rollback_sql: string | null;
  applied_at: string | null;
  applied_by: string | null;
  created_at: string | null;
}

export const MIGRATION_STATUS_META: Record<string, { label: string; className: string }> = {
  pending: { label: 'En attente', className: 'bg-background-200/70 text-foreground-600' },
  applied: { label: 'Appliquée', className: 'bg-secondary-100 text-secondary-900' },
  failed: { label: 'Échec', className: 'bg-red-100 text-red-800' },
  rolled_back: { label: 'Annulée', className: 'bg-accent-100 text-accent-900' },
};

export function migrationStatusMeta(key: string | null | undefined) {
  return MIGRATION_STATUS_META[key || 'pending'] || MIGRATION_STATUS_META.pending;
}

export async function listMigrations(): Promise<CoreMigration[]> {
  const { data, error } = await supabase
    .from('core_migrations')
    .select('*')
    .order('version', { ascending: true });
  if (error) throw error;
  return (data || []) as CoreMigration[];
}

/** Génère une version horodatée : 2026_10_05_006 */
export function nextVersion(existing: CoreMigration[]): string {
  const today = new Date().toISOString().slice(0, 10).replace(/-/g, '_');
  const sameDay = existing.filter((m) => m.version.startsWith(today));
  const seq = String(sameDay.length + 1).padStart(3, '0');
  return `${today}_${seq}`;
}

function checksum(value: string): string {
  let h = 0;
  for (let i = 0; i < value.length; i += 1) h = (Math.imul(31, h) + value.charCodeAt(i)) | 0;
  return Math.abs(h).toString(16).padStart(8, '0');
}

export interface CreateMigrationInput {
  version: string;
  name: string;
  description?: string | null;
  rollbackSql?: string | null;
}

export async function createMigration(input: CreateMigrationInput): Promise<void> {
  const { error } = await supabase.from('core_migrations').insert({
    version: input.version,
    name: input.name,
    description: input.description ?? null,
    rollback_sql: input.rollbackSql ?? null,
    checksum: checksum(`${input.version}:${input.name}`),
    status: 'pending',
  });
  if (error) throw error;
}

export async function applyMigration(id: number, appliedBy = 'manual'): Promise<void> {
  const { error } = await supabase.from('core_migrations').update({
    status: 'applied',
    applied_at: new Date().toISOString(),
    applied_by: appliedBy,
  }).eq('id', id);
  if (error) throw error;
}

export async function rollbackMigration(id: number): Promise<void> {
  const { data } = await supabase.from('core_migrations').select('rollback_sql').eq('id', id).maybeSingle();
  if (!data?.rollback_sql) throw new Error('Aucune procédure de rollback définie pour cette migration.');
  const { error } = await supabase.from('core_migrations').update({ status: 'rolled_back' }).eq('id', id);
  if (error) throw error;
}

export async function deleteMigration(id: number): Promise<void> {
  const { error } = await supabase.from('core_migrations').delete().eq('id', id).neq('status', 'applied');
  if (error) throw error;
}

export const MIGRATION_PRINCIPLES: string[] = [
  'Chaque changement de schéma a un numéro de version horodaté.',
  'Une migration est appliquée de façon contrôlée, jamais à la main.',
  'Un rollback est défini lorsque c’est techniquement possible.',
  'Chaque migration est documentée (description + auteur + date).',
];

export interface MigrationStats {
  total: number;
  applied: number;
  pending: number;
  rollbacks: number;
}

export async function fetchMigrationStats(): Promise<MigrationStats> {
  const migrations = await listMigrations();
  return {
    total: migrations.length,
    applied: migrations.filter((m) => m.status === 'applied').length,
    pending: migrations.filter((m) => !m.status || m.status === 'pending').length,
    rollbacks: migrations.filter((m) => m.rollback_sql).length,
  };
}